import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, Ban, Brush, Shirt, Gem, Scissors, Heart } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useProfile } from "@/context/ProfileContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Avatar from "@/components/Avatar";
import ProductCard from "@/components/ProductCard";
import AdSlot from "@/components/AdSlot";
import Seo from "@/components/Seo";
import AiInsight from "@/components/AiInsight";

const TABS = [
  { key: "makeup", label_it: "Make-up", label_en: "Make-up" },
  { key: "skincare", label_it: "Skincare", label_en: "Skincare" },
  { key: "fashion", label_it: "Fashion", label_en: "Fashion" },
];

export default function Results() {
  const { t, L, lang } = useI18n();
  const { profile, season, options, analyze, hasProfile } = useProfile();
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("makeup");
  const [recs, setRecs] = useState({});
  const [loading, setLoading] = useState(true);
  const [allSeasons, setAllSeasons] = useState([]);

  useEffect(() => { api.get("/seasons").then(({ data }) => setAllSeasons(data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!hasProfile) { setLoading(false); return; }
    if (!season) analyze(profile);
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.post("/recommend", { profile, domain: tab, limit: 12 });
        setRecs((r) => ({ ...r, [tab]: data.items }));
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [tab, hasProfile]);

  if (!hasProfile) {
    return (
      <div className="max-w-xl mx-auto px-5 py-24 text-center">
        <img src="/velyrea-logo.png" alt="" className="h-16 w-16 mx-auto mb-6" />
        <h1 className="font-serif text-3xl mb-3">Completa prima il quiz</h1>
        <p className="text-charcoal/55 mb-6">Abbiamo bisogno del tuo profilo per generare l'analisi armocromatica e i consigli personalizzati.</p>
        <Link to="/quiz" data-testid="results-goto-quiz" className="inline-flex rounded-full bg-rose-400 hover:bg-rose-500 text-white font-semibold px-8 py-3.5 transition-colors">Fai il quiz</Link>
      </div>
    );
  }

  const s = season;
  const lipHex = s?.palette?.[4];
  const blushHex = s?.palette?.[0];

  const saveLook = async () => {
    if (!user) { toast.error("Accedi per salvare il tuo look"); nav("/login"); return; }
    try {
      await api.post("/looks", { season_key: s?.key, season_name: L(s, "name"), profile });
      toast.success("Look salvato nel tuo profilo!");
    } catch { toast.error("Errore nel salvataggio"); }
  };

  const InfoCard = ({ icon: Icon, title, value, to, className }) => (
    <Link to={to} data-testid={`results-card-${to?.split("/").pop()}`} className={`group bg-white rounded-2xl border border-rose-100 p-6 hover-lift block ${className}`}>
      <div className="flex items-center gap-2 text-rose-500 mb-2"><Icon size={18} /><span className="text-xs uppercase tracking-widest font-semibold">{title}</span></div>
      <p className="text-charcoal/75 leading-relaxed">{value}</p>
      <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-olive-dark opacity-70 group-hover:opacity-100 transition-opacity">
        {t("common.discover")} →
      </span>
    </Link>
  );

  return (
    <div className="bg-surface min-h-screen">
      <Seo title={s ? `${L(s, "name")} — La tua armocromia` : "Risultati"} description="La tua analisi armocromatica personalizzata con palette, prodotti e prova trucco." />
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-10 pb-8">
        <div className="grid lg:grid-cols-[300px_1fr] gap-8 items-center">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl border border-rose-100 p-4 mx-auto">
            <Avatar profile={profile} options={options} lip={lipHex} blush={blushHex} size={280} />
          </motion.div>
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-rose-500 font-semibold">{t("results.title")}</p>
            <h1 className="mt-2 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight">{L(s, "name")}</h1>
            <div className="mt-3 flex items-center gap-3">
              <span className="text-sm font-bold text-white bg-gold-dark rounded-full px-3 py-1" data-testid="season-match">{s?.match}% match</span>
              <span className="text-sm text-charcoal/50">{lang === "it" ? s?.family_it : s?.family_en}</span>
            </div>
            <p className="mt-4 text-lg text-charcoal/60 max-w-xl leading-relaxed">{L(s, "desc")}</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <button onClick={saveLook} data-testid="save-look-btn" className="inline-flex items-center gap-2 rounded-full bg-rose-400 hover:bg-rose-500 text-white font-semibold px-6 py-3 transition-colors"><Heart size={16} /> Salva look</button>
              <Link to="/quiz" className="inline-flex items-center rounded-full border border-charcoal/15 px-6 py-3 font-semibold hover:border-rose-300 transition-colors">Rifai il quiz</Link>
            </div>
          </div>
        </div>
      </section>

      {/* AI INSIGHT */}
      {s && (
        <section className="max-w-7xl mx-auto px-5 md:px-8 py-4">
          <AiInsight
            cacheKey={s.key}
            title={lang === "it" ? "La tua analisi personalizzata" : "Your personalized analysis"}
            prompt={lang === "it"
              ? `Spiega in modo caloroso e sintetico la mia stagione armocromatica "${L(s, "name")}" e dammi 3 consigli pratici: un colore da indossare assolutamente, un consiglio make-up e un errore da evitare. Massimo 140 parole.`
              : `Warmly and concisely explain my color season "${L(s, "name")}" and give me 3 practical tips: one color I must wear, one make-up tip and one mistake to avoid. Max 140 words.`}
          />
        </section>
      )}

      {/* PALETTE */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-6">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl border border-rose-100 p-6" data-testid="palette-block">
            <div className="flex items-center gap-2 text-rose-500 mb-4"><Sparkles size={18} /><span className="text-xs uppercase tracking-widest font-semibold">{t("results.palette")}</span></div>
            <div className="grid grid-cols-8 gap-2">
              {(s?.palette || []).map((c, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }} className="aspect-square rounded-lg shadow-sm" style={{ background: c }} title={c} />
              ))}
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-rose-100 p-6">
            <div className="flex items-center gap-2 text-charcoal/60 mb-4"><Ban size={18} /><span className="text-xs uppercase tracking-widest font-semibold">{t("results.avoid")}</span></div>
            <div className="flex gap-3">
              {(s?.avoid || []).map((c, i) => (
                <div key={i} className="relative w-14 h-14 rounded-lg" style={{ background: c }}>
                  <Ban size={20} className="absolute inset-0 m-auto text-white/80" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ALL SEASON PALETTES */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-6">
        <h2 className="font-serif text-2xl sm:text-3xl mb-1">{lang === "it" ? "Le palette di tutte le stagioni" : "Palettes of every season"}</h2>
        <p className="text-charcoal/55 mb-5 text-sm">{lang === "it" ? "La tua è evidenziata. Esplora anche le altre armocromie." : "Yours is highlighted. Explore the other seasons too."}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="all-palettes">
          {allSeasons.map((sea) => {
            const active = sea.key === s?.key;
            return (
              <div key={sea.key} data-testid={`palette-${sea.key}`} className={`rounded-2xl border p-4 bg-white transition-all ${active ? "border-gold ring-2 ring-gold/40" : "border-rose-100"}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-serif text-lg">{L(sea, "name")}</h3>
                  {active && <span className="text-[10px] font-bold text-white bg-gold-dark rounded-full px-2 py-0.5">{lang === "it" ? "LA TUA" : "YOURS"}</span>}
                </div>
                <div className="grid grid-cols-8 gap-1.5">
                  {sea.palette.map((c, i) => <span key={i} className="aspect-square rounded" style={{ background: c }} />)}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* RECOMMENDATION TEXT BENTO */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-6">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <InfoCard icon={Brush} title={t("results.makeup")} value={L(s?.makeup, "")} to="/studio/makeup" />
          <InfoCard icon={Shirt} title={t("results.clothes")} value={L(s?.clothes, "")} to="/studio/clothes" />
          <InfoCard icon={Sparkles} title={t("results.accessories")} value={L(s?.accessories, "")} to="/studio/accessories" />
          <InfoCard icon={Gem} title={t("results.jewelry")} value={L(s?.jewelry, "")} to="/studio/jewelry" />
          <InfoCard icon={Scissors} title={t("results.hair")} value={L(s?.hair, "")} to="/studio/hair" className="md:col-span-2 lg:col-span-2" />
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-4"><AdSlot format="leaderboard" /></div>

      {/* PRODUCTS */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-8">
        <h2 className="font-serif text-3xl sm:text-4xl mb-6">{t("results.recos")}</h2>
        <div className="flex gap-2 mb-6">
          {TABS.map((tb) => (
            <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`tab-${tb.key}`}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === tb.key ? "bg-charcoal text-white" : "bg-white border border-rose-100 hover:border-rose-300"}`}>
              {L(tb, "label")}
            </button>
          ))}
        </div>
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[3/4] rounded-xl bg-white animate-pulse border border-rose-50" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="recommendation-grid">
            {(recs[tab] || []).map((p, i) => <ProductCard key={p.sku} product={p} index={i} />)}
          </div>
        )}
      </section>
    </div>
  );
}
